import { useEffect, useState } from "react";
import { Mail, Send, CheckCircle2, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { requestPasswordReset } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/AuthContext";

export default function ForgotPasswordDialog({ open, onOpenChange, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-fill with whatever the user typed in the login form when the modal opens,
  // and reset state whenever the dialog is (re-)opened.
  useEffect(() => {
    if (open) {
      setEmail(defaultEmail || "");
      setSent(false);
      setSubmitting(false);
    }
  }, [open, defaultEmail]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await requestPasswordReset(trimmed);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not request reset");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="forgot-password-dialog"
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[460px]"
      >
        <DialogHeader>
          <DialogTitle className="fc-heading text-[#1C1C1C]">Reset your password</DialogTitle>
          <DialogDescription className="text-xs text-[#A8A29E]">
            {sent
              ? "If that email matches an account, we've just sent you a password-reset link that's valid for 24 hours. Check your inbox (and spam folder)."
              : "Enter the email you use to sign in and we'll send you a secure link to set a new password."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-4 flex flex-col items-center text-center" data-testid="forgot-password-sent">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 grid place-items-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="fc-heading text-[#1C1C1C] mt-4 text-[15px]">Check your inbox</div>
            <p className="text-xs text-[#78716C] mt-1 max-w-[340px]">
              We've sent password-reset instructions to <strong className="text-[#1C1C1C]">{email}</strong> if that address is linked to an account.
            </p>
            <Button
              type="button"
              data-testid="forgot-password-done-btn"
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-10 px-5 inline-flex items-center gap-1.5"
            >
              Back to sign in <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-1">
            <div>
              <label className="fc-label mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#A8A29E] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  data-testid="forgot-password-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="fc-input h-11 pl-10"
                  autoFocus
                  required
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                data-testid="forgot-password-cancel-btn"
                onClick={() => onOpenChange(false)}
                className="text-sm text-[#78716C] hover:text-[#1C1C1C] px-3 py-1.5"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="forgot-password-submit-btn"
                disabled={submitting}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-10 px-5 inline-flex items-center gap-1.5"
              >
                {submitting ? "Sending…" : (<>Send reset link <Send className="w-4 h-4" /></>)}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
